import {
  QBankStructureLevelCategory,
  useGetQBankStructureLevelItemsWithQIdByQBankStructureIdQuery,
  useGetQBankStructureLevelsByQBankStructureIdQuery,
} from '@exam-management/graphql';
import {
  Box,
  Flex,
  EllipsisText,
  MoreMenuIconButton,
  DeleteOutlineIcon,
  ConfirmationModal,
  Text,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useMemo, useState } from 'react';

import { type QBankStructureSettingSectionTableData, QBankStructureTable } from '../../components';
import { useQConfigGeneratorCreationForm } from '../../providers';
import { PartSection } from '../part-section';
import { getChildrenQuestionCount } from '../qbank-structure-table-body';

import {
  findChildrenIds,
  mapSelectedLevelItems,
  sortLevelItems,
  type QBankStructureLevelItemWithLevel,
} from './utils';

interface QBankStructureSettingSectionProps {
  qBankStructure: {
    qbankSelection: {
      value: string | null;
      label: string | null;
    } | null;
    levelItems: Record<string, boolean> | null;
  };
}

export default function QBankStructureSettingSection(
  props: Readonly<QBankStructureSettingSectionProps>,
) {
  const { qBankStructure } = props;
  const formContext = useQConfigGeneratorCreationForm();
  const { watch } = formContext;
  const [deleteQBankLevelItemId, setDeleteQBankLevelItemId] = useState<string | null>(null);
  const [isDeleteModalOpen, deleteModalHandlers] = useDisclosure();
  const { open: openDeleteModal, close: closeDeleteModal } = deleteModalHandlers;

  const chapterQuestionDistribution = watch('chapterQuestionDistribution');
  const qbank = watch('qbank') ?? [];

  const { data: getQBSLevelItemsResult } =
    useGetQBankStructureLevelItemsWithQIdByQBankStructureIdQuery({
      variables: {
        qBankStructureId: qBankStructure?.qbankSelection?.value ?? '',
        options: {
          categories: [
            QBankStructureLevelCategory.Course,
            QBankStructureLevelCategory.Part,
            QBankStructureLevelCategory.Subject,
            QBankStructureLevelCategory.Group,
            QBankStructureLevelCategory.Chapter,
          ],
        },
      },
      skip: !qBankStructure?.qbankSelection?.value,
    });

  const qBankStructureLevelItems = useMemo(
    () => getQBSLevelItemsResult?.qbsGetQBankStructureLevelItemsByQBankStructureId ?? [],
    [getQBSLevelItemsResult],
  );

  const courseLevelItemId =
    qBankStructureLevelItems.find((item) => item.parentLevelItemId === null)?.id ?? '';

  const { data: getQBSLevelResult } = useGetQBankStructureLevelsByQBankStructureIdQuery({
    variables: {
      qBankStructureId: qBankStructure?.qbankSelection?.value || '',
    },
  });

  const qBankStructureLevels = useMemo(
    () => getQBSLevelResult?.qbsGetQBankStructureLevelsByQBankStructureId ?? [],
    [getQBSLevelResult],
  );

  const filtered = useMemo(() => {
    return Object.keys(qBankStructure?.levelItems || {}).filter(
      (key) => qBankStructure?.levelItems?.[key],
    );
    // REMARK: re-render when qBankStructure.levelItems changes for update display
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qBankStructure, chapterQuestionDistribution]);

  const selectedLevelItemsWithLevel = useMemo(() => {
    if (!qBankStructure) return [];

    return filtered
      .map((id) => {
        const item = qBankStructureLevelItems.find((i) => i.id === id);
        if (!item) return null;
        const qBankStructureLevel = qBankStructureLevels.find(
          (level) => level.id === item.qBankStructureLevelId,
        );
        if (!qBankStructureLevel) return null;
        return {
          ...item,
          qBankStructureLevel,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [filtered, qBankStructureLevelItems, qBankStructureLevels, qBankStructure]);

  const allLevelItems = useMemo(() => {
    return qBankStructureLevelItems
      .map((item) => {
        const qBankStructureLevel = qBankStructureLevels.find(
          (level) => level.id === item.qBankStructureLevelId,
        );
        if (!qBankStructureLevel) return null;
        return {
          ...item,
          qBankStructureLevel,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [qBankStructureLevelItems, qBankStructureLevels]);

  const formattedData = useMemo(
    () =>
      mapSelectedLevelItems(
        selectedLevelItemsWithLevel as QBankStructureLevelItemWithLevel[],
        allLevelItems as QBankStructureLevelItemWithLevel[],
      ),
    [selectedLevelItemsWithLevel, allLevelItems],
  );

  const deleteQBankStructureCategory = useMemo(() => {
    return formattedData.find((item) => item.id === deleteQBankLevelItemId)?.level ?? '';
  }, [formattedData, deleteQBankLevelItemId]);

  const handleDeleteMenu = (input: string) => {
    setDeleteQBankLevelItemId(input);
    openDeleteModal();
  };

  const handleDeleteQbankStructure = () => {
    if (!deleteQBankLevelItemId) {
      return;
    }

    const childrenIds = findChildrenIds(formattedData, deleteQBankLevelItemId);
    const filteredLevelItems = qBankStructure?.levelItems ?? {};
    const updatedLevelItems = Object.keys(filteredLevelItems).reduce(
      (prev, curr) => {
        if (
          curr === deleteQBankLevelItemId ||
          [...childrenIds, deleteQBankLevelItemId].includes(curr)
        ) {
          return prev;
        }
        return {
          ...prev,
          [curr]: filteredLevelItems[curr],
        };
      },
      {} as Record<string, boolean>,
    );

    const qbankIndex = qbank.findIndex(
      (qbs) => qbs.qbankSelection?.value === qBankStructure?.qbankSelection?.value,
    );
    const chapterQuestionDistribution = watch('chapterQuestionDistribution') || {};
    const newChapterQuestionDistribution = Object.fromEntries(
      Object.entries(chapterQuestionDistribution).filter(
        ([id]) => ![...childrenIds, deleteQBankLevelItemId].includes(id),
      ),
    );

    formContext.setValue('chapterQuestionDistribution', newChapterQuestionDistribution);
    formContext.setValue(`qbank.${qbankIndex}.levelItems`, updatedLevelItems);

    if (deleteQBankLevelItemId === courseLevelItemId) {
      formContext.setValue('qbank', [
        ...qbank.slice(0, qbankIndex),
        ...qbank.slice(qbankIndex + 1),
      ]);
    }

    closeDeleteModal();
  };

  const partData = formattedData
    .filter((data) => data.level === QBankStructureLevelCategory.Part)
    .sort((a, b) => a.order - b.order);

  return (
    <>
      <Flex
        sx={(theme) => ({
          p: theme.spacing(6),
          flexDirection: 'column',
          gap: theme.spacing(4),
        })}
      >
        <Flex
          sx={(theme) => ({
            bgcolor: theme.palette.common.black,
            color: theme.palette.common.white,
            p: theme.spacing(2.5, 4),
            borderRadius: theme.shape.rounded(2),
            minHeight: '56px',
            alignItems: 'center',
          })}
        >
          <EllipsisText
            variant='h4'
            sx={{
              flex: 1,
            }}
          >
            {qBankStructure?.qbankSelection?.label}
          </EllipsisText>
          <MoreMenuIconButton
            options={[
              {
                label: 'Remove',
                icon: <DeleteOutlineIcon sx={(theme) => ({ color: theme.palette.error.main })} />,
                sx: (theme) => ({ color: theme.palette.error.main }),
                onClick: () => handleDeleteMenu(courseLevelItemId),
              },
            ]}
            slotProps={{
              iconButton: { sx: { color: 'inherit' } },
            }}
          />
        </Flex>
        {partData.map((part) => {
          const data =
            (formattedData.filter((data) =>
              data.parentIds.includes(part.id),
            ) as QBankStructureSettingSectionTableData[]) ??
            ([] as QBankStructureSettingSectionTableData[]);
          const { childrenQuestionCount } = getChildrenQuestionCount({
            id: part.id,
            rows: data,
            chapterQuestionDistribution,
          });
          const sortedData = sortLevelItems(data, part.id);
          return (
            <PartSection
              key={part.id}
              partData={{
                name: part.levelName ?? '',
                id: part.id,
                totalQuestions: childrenQuestionCount,
              }}
              handleDeleteMenu={handleDeleteMenu}
            >
              <Box sx={{ pt: (theme) => theme.spacing(3) }}>
                <QBankStructureTable data={sortedData} handleDeleteMenu={handleDeleteMenu} />
              </Box>
            </PartSection>
          );
        })}
      </Flex>
      <ConfirmationModal
        title={`Remove ${
          deleteQBankStructureCategory && deleteQBankStructureCategory.trim()
            ? deleteQBankStructureCategory.charAt(0).toUpperCase() +
              deleteQBankStructureCategory.slice(1).toLowerCase()
            : 'item'
        }?`}
        open={isDeleteModalOpen}
        acceptText='Remove'
        variant='error'
        content={
          <Text>
            การลบรายการนี้จะทำให้ข้อมูล
            <Text variant='body1Semibold' component='span'>
              “จำนวนข้อสอบที่กำหนดไว้ในแต่ละบท”{` `}
            </Text>
            ถูกลบทั้งหมด กรุณาตรวจสอบก่อนดำเนินการ
          </Text>
        }
        onDiscard={closeDeleteModal}
        onClose={closeDeleteModal}
        onAccept={handleDeleteQbankStructure}
      />
    </>
  );
}
